import { appConfigService } from '@/providers/services/app/app-config';
import { PERMISSION_LIST, PROFICIENCY_ROUTE_VIEW, ROLE_MENUS, USER_TYPE } from '@/utils/constants';
import { authAPI } from '@/providers/apis/auth/auth';
import { sessionService } from '@/providers/services/auth/session';
import { appConfigAPI } from '@/providers/apis/app/app-config';
import router from '@/routers/router';
import { TenantAccountListModel } from '@/models/auth/session';
import { tenantSettingsAPI } from '@/providers/apis/tenant-settings/tenant-settings';
import StaticAssetsMixin from '@/helpers/mixins/static-assets-mixin';
/**
 * Permission class help to show UI based on users permissions
 */
export class Permission {

    private static INSTANCE = new Permission();

    static get instance() {
        return this.INSTANCE;
    }

    public get userRole() {
        return appConfigService.getAppUserRole();
    }

    /**
     * Help to identify roles by pages
     */
    public ACL = PERMISSION_LIST;

    /**
     * Help to defined the menu list
     */
    public menus = ROLE_MENUS;

    public GROUP_TENANTS_KEY = 'GROUP_HIERARCHY_TENANTS';

    public ASSESSIBILITY_SETTINGS_KEY = 'accessibility_settings';

    public GROUP_HIERARCHYS = 'USER_GROUP_HIERARCHYS_LIST';

    public CLASS_DRILLDOWN = 'CLASS_DRILLDOWN';

    /**
     * Show the menus based on permission
     * @param value
     */
    public hasMenuAccess(value: any) {
        return this.userRole.menus.indexOf(value) !== -1;
    }

    /**
     * Show the pages based on permissions
     * @param component
     * @param value
     */
    public hasPermission(menu: any, value: any) {
        const userRole = this.userRole;
        return userRole.pages[menu]
            ? (userRole.pages[menu].indexOf(value) !== -1 || userRole.pages.all.indexOf('all') !== -1)
            : false;
    }

    public get isHighContrastEnable() {
        let isHighContrast: any = false;
        const accessibilitySettings: any = this.getAccessibilitySetting();
        isHighContrast = accessibilitySettings &&
            accessibilitySettings.is_high_contrast_enabled
            ? accessibilitySettings.is_high_contrast_enabled
            : false;
        return isHighContrast;
    }

    public get zoomValue() {
        let zoomValue: any = 1;
        const accessibilitySettings: any = this.getAccessibilitySetting();
        zoomValue = accessibilitySettings &&
            accessibilitySettings.screen_zoom_value
            ? accessibilitySettings.screen_zoom_value
            : 1;
        return zoomValue;
    }

    public getUserType() {
        let userType;
        if (sessionService.getSession()!.permissions.indexOf('ALL_ACCESS') !== -1) {
            userType = USER_TYPE.superUser;
        } else if (sessionService.getSession()!.permissions.indexOf('VIEW_LR_AS_DU') !== -1) {
            userType = USER_TYPE.districtUser;
        } else if (sessionService.getSession()!.permissions.indexOf('VIEW_LR_AS_CP') !== -1) {
            userType = USER_TYPE.contentProvider;
        }
        return userType;
    }

    /**
     * Show the pages based on permissions
     * @param component
     * @param value
     */
    public hasPageAccess(menu: any, value: any) {
        const userRole = this.userRole;
        return userRole.pages[menu]
            ? (userRole.pages[menu].indexOf(value) !== -1)
            : false;
    }

    public isGuestUser(menu: any, value: any) {
        const userRole = this.userRole;
        return userRole.pages[menu].indexOf(value) !== -1;
    }

    public get isDemoGroup() {
        const session = sessionService.getSession();
        const userItems = appConfigService.demoUserList();
        return userItems.indexOf(session!.user_id) !== -1;
    }

    public get isShowcaseDemo() {
        const session = sessionService.getSession();
        const userDetails = appConfigService.showcaseDemoUsers();
        return userDetails.indexOf(session!.user_id) !== -1;
    }

    private get hasProficiencyRoute() {
        const session = sessionService.getSession();
        const userId = session!.user_id;
        return PROFICIENCY_ROUTE_VIEW.indexOf(userId) !== -1;
    }

    public get isDemoTenant() {
        const session = sessionService.getSession();
        const userId = session!.user_id;
        const users = appConfigService.demoTenant();
        return users.indexOf(userId) !== -1;
    }

    /**
     * It hold the landing page for the tenant user
     */
    public landingPage() {
        return this.userRole.landingPage;
    }

    /**
     * help to login as a demo user
     * @param userDetails holding user login credentials
     */
    public async doLoginInWithCredential(userDetails: any) {
        const username = userDetails.username;
        const password = userDetails.password;
        const accessToken =  userDetails.accesToken;
        await authAPI
            .logInWithCredential(username, password, accessToken)
            .then(
                (session) => {
                    this.updateRole(session);
                },
            );
    }

    public async fetchTenantList(email: string, token: string) {
        return await authAPI.getAccountsLogin(email, token);
    }

    public async doLoginInTenantId(tenant: TenantAccountListModel) {
        return await authAPI.getTenantLoginType(tenant);
    }

    public async getLoginType(nonce: string) {
        return authAPI.tenantLoginType(nonce);
    }

    public getAssets(cdnUrls: any, fileName= 'logo') {
        return new StaticAssetsMixin().getAssets(cdnUrls, fileName);
    }

    public getLoginPageInfo() {
        return appConfigService.getLoginPageInfo();
    }

    public getIsMygooruHost() {
        return new StaticAssetsMixin().isMygooruHost();
    }

    public setFavIcon(favIconUrl: string) {
        (() => {
            const link: any = document.querySelector('link[rel*=\'icon\']') || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = favIconUrl;
            document.getElementsByTagName('head')[0].appendChild(link);
        })();
    }

    public updateRole(session: any, isReload: boolean = true) {
        if (!sessionService.getDemoSessionCopy()) {
            sessionService.setDemoSessionCopy();
        }
        return appConfigAPI.getAppPermissions(session.permissions).then((userRole: any) => {
            tenantSettingsAPI.fetchActiveTenantSettings(session.access_token).then((tenantSettings) => {
                const sessionSave: any = session;
                sessionSave.tenant.settings = tenantSettings;
                if (userRole) {
                    appConfigService.setAppUserRole(userRole);
                    sessionService.setSession(sessionSave);
                    userRole.landingPage = tenantSettings.mc_default_landing_page || userRole.landingPage;
                    if (isReload) {
                        window.location.href = window.location.origin + userRole.landingPage;
                    }
                    // Remove once the ember js not used on mission control
                    if (sessionService.getMcUpdate()) {
                        sessionService.deleteMcUpdate();
                    }
                    return userRole.landingPage;
                }
            });
        });
    }

    public loginBasedOnToken(token: string, queryParam: any = {}) {
        return authAPI
            .signInWithToken(token)
            .then(
                (session: any) => {
                    if (session === 401) {
                        return session;
                    } else {
                        return appConfigAPI.getAppPermissions(session.permissions).then((userRole: any) => {
                            if (userRole) {
                                if (queryParam && queryParam.isHideRoleMenu) {
                                    session.isHideRoleMenu = JSON.parse(queryParam.isHideRoleMenu);
                                }
                                appConfigService.setAppUserRole(userRole);
                                sessionService.setSession(session);
                                if (queryParam && queryParam.redirectUrl) {
                                    document.location.href = document.location.origin + queryParam.redirectUrl;
                                } else {
                                    document.location.href = document.location.origin + userRole.landingPage;
                                }
                            }
                        });
                    }
                });
    }

    public setGroupHierarchyTenants(tenants?: string | any) {
        localStorage.setItem(this.GROUP_TENANTS_KEY, tenants);
    }

    public get getGroupHierarchyTenants() {
        return localStorage.getItem(this.GROUP_TENANTS_KEY);
    }

    public getSetHierarchyDetails(heirarchy: any = null) {
        if (heirarchy) {
            localStorage.setItem(this.GROUP_HIERARCHYS, JSON.stringify(heirarchy));
            return;
        }
        return localStorage.getItem(this.GROUP_HIERARCHYS)
            ? JSON.parse(localStorage.getItem(this.GROUP_HIERARCHYS) as any) : null;
    }

    public updateClassDrilldown(data: any = null) {
        if (data) {
            localStorage.setItem(this.CLASS_DRILLDOWN, JSON.stringify(data));
            return;
        }
        return localStorage.getItem(this.CLASS_DRILLDOWN)
            ? JSON.parse(localStorage.getItem(this.CLASS_DRILLDOWN) as any) : null;
    }

    public deleteClassDrillDown() {
        localStorage.removeItem(this.CLASS_DRILLDOWN);
    }

    public saveAccessibilitySetting(settings: any) {
        if (settings) {
            localStorage.setItem(this.ASSESSIBILITY_SETTINGS_KEY, JSON.stringify(settings));
        }
    }

    public getAccessibilitySetting() {
        return localStorage.getItem(this.ASSESSIBILITY_SETTINGS_KEY)
            ? JSON.parse(localStorage.getItem(this.ASSESSIBILITY_SETTINGS_KEY) as any) : null;
    }

}

export default Permission.instance;
